import { logger } from '@monofront/logging';
import { IProduct } from '@monofront/types';
import { SearchResponse } from 'algoliasearch-helper/types/algoliasearch';
import {
  Hit,
  LegacySearchMethodProps,
  SearchQuery,
  liteClient as algoliasearch,
} from 'algoliasearch/lite';
import { WeakProductCache } from './weakProductCache';
import { LRUCache } from 'lru-cache';

interface IAlgoliaConfig {
  app: {
    key: string;
    id: string;
  };
}

type Cache<T> = {
  get(key: string): T | undefined;
  set(key: string, item: T): void;
};

export class AlgoliaSearchClient {
  public searchClient: ReturnType<typeof algoliasearch>;
  public cache: Cache<Hit<IProduct>>;

  constructor(algoliaConfig: IAlgoliaConfig) {
    if (WeakProductCache.isSupportedByCurrentPlatform()) {
      this.cache = new WeakProductCache();
    } else {
      this.cache = new LRUCache({
        max: 90,
      });
    }

    // According to doc, cache is enabled by default with this configuration:
    // - Request: in memory
    // - Response: in memory
    // - Hosts: localStorage and fallback to in memory
    // > see https://www.algolia.com/doc/libraries/javascript/v5/caching/
    this.searchClient = algoliasearch(
      algoliaConfig.app.id,
      algoliaConfig.app.key,
    );

    // Override search methods to cache products hits
    const _search = this.searchClient.search;
    const _searchForHits = this.searchClient.searchForHits.bind(
      this.searchClient,
    ); // the searchForHits helper is purely for typing purposes that calls the search method. We bind to the overridden searchClient, so it can find a reference to this.search method and use the cache.

    this.searchClient.search = async <T>(
      ...args: Parameters<typeof _search>
    ) => {
      const res = await _search<T>(...args);

      res.results.forEach((result) => {
        // Collection and Blogposts also use this method,
        // but we only want to cache products
        if ('hits' in result && result.hits[0] && 'sku' in result.hits[0]) {
          result.hits.forEach((hit) => {
            const product = hit as unknown as Hit<IProduct>;

            this.cache.set(product.sku, product);
          });
        }
      });

      return res;
    };

    this.searchClient.searchForHits = async <T>(
      ...args: Parameters<typeof _searchForHits>
    ) => {
      // error TS2347: Untyped function calls may not accept type arguments.
      // only occurs in cl-backoffice
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const res = await _searchForHits<T>(...args); // will call the overridden search method, and so the cache.

      return res;
    };
  }
}

export class AlgoliaSearchDebouncer {
  private queries: Array<SearchQuery> = [];
  private timeout: ReturnType<typeof setTimeout> | null = null;
  private promiseResolvers: Array<(value: unknown) => void> = [];
  private closed = false;

  constructor(
    private searchClient: ReturnType<typeof algoliasearch>,
    private debounceTimeout: number,
  ) {}

  public search<TResponse>(
    query: LegacySearchMethodProps[number] & { indexName: string },
  ) {
    if (this.closed) {
      throw new Error('Tried to use algolia debouncer after it was closed');
    }

    this.refreshTimeout();
    this.queries.push(query);

    // This is where the magic happens: a new intermetiade Promise is create,
    // its resolver will we resolved in `flush()` which will give the result
    // corresponding to this query.
    return new Promise<SearchResponse<TResponse>>((resolve) => {
      this.promiseResolvers.push(resolve as (value: unknown) => void);
    });
  }

  public isClosed() {
    return this.closed;
  }

  private refreshTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    // Algolia allows only 50 queries in a multi-queries requests
    if (this.queries.length === 50) {
      void this.flush();

      return;
    }

    this.timeout = setTimeout(this.flush.bind(this), this.debounceTimeout);
  }

  private async flush() {
    this.closed = true;

    const searchResult = await this.searchClient.search(this.queries);

    // We assume that Algolia will return the results in the same order as the
    // queries were sent.
    this.promiseResolvers.forEach((resolver, index) =>
      resolver(searchResult.results[index]),
    );

    logger.info('Algolia search debouncer flushed', {
      queriesCount: this.queries.length,
    });
  }
}
